import type {
  ActivityEventType,
  LogActivityObject,
  LogActivityRequest,
} from "@websim/core/src/data/types";
import { Json } from "@websim/supabase";

export class EventLogger {
  private queue: LogActivityObject[] = [];
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_DELAY_MS = 100;

  private readonly distinctId: string | undefined;
  private readonly sessionId: string | undefined;

  constructor(args: { distinctId?: string; sessionId?: string }) {
    const { distinctId, sessionId } = args;
    this.distinctId = distinctId;
    this.sessionId = sessionId;
    // Flush queue with beacon during visibilitychange
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.flush();
      }
    });
  }

  public logEvent(
    projectId: string,
    type: ActivityEventType,
    attributes: Record<string, Json>
  ): void {
    this.queue.push({ project_id: projectId, type, attributes });
    this.scheduleBatch();
  }

  private scheduleBatch(): void {
    if (this.timeout === null) {
      this.timeout = setTimeout(() => this.flush(), this.BATCH_DELAY_MS);
    }
  }

  public async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const events: LogActivityObject[] = [...this.queue];
    this.queue = [];

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    const request: LogActivityRequest = {
      events: events.map((e) => ({
        ...e,
        attributes: { ...e.attributes, $session_id: this.sessionId },
      })),
      distinct_id: this.distinctId,
    };

    try {
      // TODO: Use navigator.sendBeacon
      await fetch("/api/v1/activity/log", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
        keepalive: true,
      });
    } catch (error) {
      console.error("Failed to send event batch:", error);
    }
  }
}
